import {
    Box,
    Chip,
    Collapse,
    Grid,
    IconButton,
    Stack,
    Tooltip,
    Typography,
    useTheme,
} from "@mui/material";
import React, { useState } from "react";
import { JsonSchemaRoot, JsonSchema } from "components/JsonSchema/types";
import {
    ExpandMore,
    ExpandLess,
    Info,
    CheckCircle,
    Cancel,
} from "@mui/icons-material";
import { TitledSection } from "components/Classifiers/Common/TitledSection";

interface SchemaBasedJSONViewerProps {
    value: unknown;
    schema: JsonSchemaRoot;
}

export function SchemaBasedJSONViewer({
    value,
    schema,
}: SchemaBasedJSONViewerProps) {
    // If value is a JSON string, parse it first
    let parsedValue = value;
    if (typeof value === "string") {
        try {
            parsedValue = JSON.parse(value);
        } catch {
            // If parsing fails, use the string as-is
            parsedValue = value;
        }
    }

    // If root is an array, render items directly without array wrapper
    if (schema.type === "array" && Array.isArray(parsedValue)) {
        const itemSchema = (schema as any).items;
        if (!itemSchema) return null;

        return (
            <Stack rowGap={2} width="100%">
                {parsedValue.map((item, index) => (
                    <SchemaField
                        key={index}
                        value={item}
                        schema={itemSchema}
                        fieldName={`${itemSchema.title ?? "Element"} #${
                            index + 1
                        }`}
                        level={0}
                    />
                ))}
            </Stack>
        );
    }

    // If root is an object, render properties directly without object wrapper
    if (
        schema.type === "object" &&
        parsedValue &&
        typeof parsedValue === "object" &&
        !Array.isArray(parsedValue)
    ) {
        const properties = schema.properties || {};
        const objectValue = parsedValue as Record<string, unknown>;

        return (
            <Grid container columns={2} columnSpacing={2} rowSpacing={1}>
                {Object.entries(properties)
                    .sort(([a], [b]) => a.localeCompare(b, undefined, { sensitivity: "base" }))
                    .map(([key, propSchema]) => {
                        const propValue = objectValue[key];
                        const name = propSchema.title ?? key;

                        return (
                            <Grid item xs={1} key={key}>
                                <SchemaField
                                    value={propValue}
                                    schema={propSchema}
                                    fieldName={name}
                                    level={0}
                                />
                            </Grid>
                        );
                    })}
            </Grid>
        );
    }

    return (
        <Box>
            <SchemaField
                value={parsedValue}
                schema={schema}
                fieldName={schema.title || "Root"}
            />
        </Box>
    );
}

interface SchemaFieldProps {
    value: unknown;
    schema: JsonSchema;
    fieldName: string;
    level?: number;
}

function SchemaField({
    value,
    schema,
    fieldName,
    level = 0,
}: SchemaFieldProps) {
    const theme = useTheme();
    const [expanded, setExpanded] = useState(schema.type === "object"); // Start collapsed by default

    const paddingLeft = level * 2;

    // Handle null values
    if (value === null || value === undefined) {
        return (
            <Stack
                direction="row"
                alignItems="center"
                gap={1}
                paddingLeft={paddingLeft}
                paddingY={0.5}
            >
                <Typography
                    variant="body2"
                    fontWeight={600}
                    color={theme.palette.gray[700]}
                >
                    {fieldName}:
                </Typography>
                <Typography
                    variant="body2"
                    color={theme.palette.gray[500]}
                    fontStyle="italic"
                >
                    null
                </Typography>
                {schema.description && (
                    <Tooltip title={schema.description} arrow>
                        <Info fontSize="small" color="action" />
                    </Tooltip>
                )}
            </Stack>
        );
    }

    // Render based on schema type
    switch (schema.type) {
        case "object":
            return (
                <ObjectField
                    value={value as Record<string, unknown>}
                    schema={schema}
                    fieldName={fieldName}
                    level={level}
                    expanded={expanded}
                    onToggleExpand={() => setExpanded(!expanded)}
                />
            );

        case "array":
            return (
                <ArrayField
                    value={value as unknown[]}
                    schema={schema}
                    fieldName={fieldName}
                    level={level}
                    expanded={expanded}
                    onToggleExpand={() => setExpanded(!expanded)}
                />
            );

        case "boolean":
            return (
                <BooleanField
                    value={value as boolean}
                    schema={schema}
                    fieldName={fieldName}
                    level={level}
                />
            );

        case "string":
        case "number":
        case "integer":
            return (
                <PrimitiveField
                    value={value}
                    schema={schema}
                    fieldName={fieldName}
                    level={level}
                />
            );

        default:
            return (
                <PrimitiveField
                    value={value}
                    schema={schema}
                    fieldName={fieldName}
                    level={level}
                />
            );
    }
}

interface ObjectFieldProps {
    value: Record<string, unknown>;
    schema: JsonSchema;
    fieldName: string;
    level: number;
    expanded: boolean;
    onToggleExpand: () => void;
}

function ObjectField({
    value,
    schema,
    fieldName,
    level,
    expanded,
    onToggleExpand,
}: ObjectFieldProps) {
    const theme = useTheme();

    if (schema.type !== "object") return null;

    const properties = schema.properties || {};
    // const required = schema.required || [];

    return (
        <TitledSection
            variant="bordered"
            collapsible
            divider={false}
            title={
                <Stack direction="row" alignItems="center" columnGap={1}>
                    <Typography
                        fontWeight={600}
                        color={theme.palette.gray[700]}
                    >
                        {fieldName}
                    </Typography>
                    {schema.description && (
                        <Tooltip title={schema.description} arrow>
                            <Info fontSize="small" color="action" />
                        </Tooltip>
                    )}
                </Stack>
            }
        >
            <Grid
                container
                columns={2}
                columnSpacing={2}
                rowSpacing={1}
                paddingTop={-1}
            >
                {Object.entries(properties)
                    .sort(([a], [b]) => a.localeCompare(b, undefined, { sensitivity: "base" }))
                    .map(([key, propSchema]) => {
                        // const isRequired = required.includes(key);
                        const propValue = value?.[key];

                        const name = propSchema.title ?? key;

                        return (
                            <Grid item xs={1} key={key}>
                                <SchemaField
                                    value={propValue}
                                    schema={propSchema}
                                    fieldName={name}
                                    level={level + 1}
                                />
                            </Grid>
                        );
                    })}
            </Grid>
        </TitledSection>
    );
}

interface ArrayFieldProps {
    value: unknown[];
    schema: JsonSchema;
    fieldName: string;
    level: number;
    expanded: boolean;
    onToggleExpand: () => void;
}

function ArrayField({
    value,
    schema,
    fieldName,
    level,
    expanded,
    onToggleExpand,
}: ArrayFieldProps) {
    const theme = useTheme();
    const paddingLeft = level * 2;

    if (schema.type !== "array") return null;

    const itemCount = Array.isArray(value) ? value.length : 0;
    const itemSchema = schema.items;

    if (!itemSchema) return null;

    return (
        <Box>
            <Stack
                direction="row"
                alignItems="center"
                gap={1}
                paddingLeft={paddingLeft}
                paddingY={0.5}
                sx={{
                    backgroundColor:
                        level % 2 === 0
                            ? "transparent"
                            : theme.palette.gray[50],
                    borderLeft:
                        level > 0
                            ? `2px solid ${theme.palette.gray[200]}`
                            : "none",
                }}
            >
                <IconButton
                    size="small"
                    onClick={onToggleExpand}
                    sx={{ padding: 0.5 }}
                >
                    {expanded ? (
                        <ExpandLess fontSize="small" />
                    ) : (
                        <ExpandMore fontSize="small" />
                    )}
                </IconButton>
                <Typography
                    variant="body2"
                    fontWeight={600}
                    color={theme.palette.gray[700]}
                >
                    {fieldName}
                </Typography>
                <Chip
                    label={`${itemCount} ${itemCount === 1 ? "item" : "items"}`}
                    size="small"
                    sx={{
                        height: 20,
                        fontSize: "0.7rem",
                        backgroundColor: theme.palette.purple[100],
                        color: theme.palette.purple[700],
                    }}
                />
                {schema.description && (
                    <Tooltip title={schema.description} arrow>
                        <Info fontSize="small" color="action" />
                    </Tooltip>
                )}
            </Stack>
            <Collapse in={expanded}>
                <Box>
                    {Array.isArray(value) &&
                        value.map((item, index) => (
                            <Box key={index}>
                                <SchemaField
                                    value={item}
                                    schema={itemSchema}
                                    fieldName={`${
                                        itemSchema.title ?? "Element"
                                    } #${index + 1}`}
                                    level={level + 1}
                                />
                            </Box>
                        ))}
                </Box>
            </Collapse>
        </Box>
    );
}

interface BooleanFieldProps {
    value: boolean;
    schema: JsonSchema;
    fieldName: string;
    level: number;
}

function BooleanField({ value, schema, fieldName, level }: BooleanFieldProps) {
    const theme = useTheme();
    const paddingLeft = level * 2;

    return (
        <Stack
            direction="column"
            gap={0.5}
            paddingLeft={paddingLeft}
            paddingY={0.5}
        >
            <Stack direction="row" alignItems="center" gap={0.5}>
                <Typography
                    variant="body2"
                    fontWeight={500}
                    color={theme.palette.gray[500]}
                    fontSize="13px"
                >
                    {fieldName}
                </Typography>
                {schema.description && (
                    <Tooltip title={schema.description} arrow>
                        <Info fontSize="small" color="action" />
                    </Tooltip>
                )}
            </Stack>
            <Stack direction="row" alignItems="center" gap={1}>
                {value ? (
                    <CheckCircle
                        fontSize="small"
                        sx={{ color: theme.palette.green[600] }}
                    />
                ) : (
                    <Cancel
                        fontSize="small"
                        sx={{ color: theme.palette.red[600] }}
                    />
                )}
                <Typography
                    variant="body2"
                    color={
                        value
                            ? theme.palette.green[700]
                            : theme.palette.red[700]
                    }
                    fontWeight={500}
                >
                    {String(value)}
                </Typography>
            </Stack>
        </Stack>
    );
}

interface PrimitiveFieldProps {
    value: unknown;
    schema: JsonSchema;
    fieldName: string;
    level: number;
}

function PrimitiveField({
    value,
    schema,
    fieldName,
    level,
}: PrimitiveFieldProps) {
    const theme = useTheme();
    const paddingLeft = level * 2;
    const [expanded, setExpanded] = useState(false);

    // Handle multiline strings
    const isMultilineString = typeof value === "string" && value.includes("\n");

    if (isMultilineString) {
        const lines = (value as string).split("\n");
        const preview = lines[0] + (lines.length > 1 ? "..." : "");

        return (
            <Stack
                direction="column"
                gap={0.5}
                paddingLeft={paddingLeft}
                paddingY={0.5}
            >
                <Stack direction="row" alignItems="center" gap={0.5}>
                    <Typography
                        variant="body2"
                        fontWeight={500}
                        color={theme.palette.gray[500]}
                        fontSize="13px"
                    >
                        {fieldName}
                    </Typography>
                    {schema.description && (
                        <Tooltip title={schema.description} arrow>
                            <Info fontSize="small" color="action" />
                        </Tooltip>
                    )}
                </Stack>
                <Stack direction="row" alignItems="center" gap={1}>
                    <IconButton
                        size="small"
                        onClick={() => setExpanded(!expanded)}
                        sx={{ padding: 0.5 }}
                    >
                        {expanded ? (
                            <ExpandLess fontSize="small" />
                        ) : (
                            <ExpandMore fontSize="small" />
                        )}
                    </IconButton>
                    {!expanded && (
                        <Typography
                            variant="body2"
                            color={theme.palette.gray[600]}
                            sx={{
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                flex: 1,
                            }}
                        >
                            {preview}
                        </Typography>
                    )}
                    <Chip
                        label={`${lines.length} lines`}
                        size="small"
                        sx={{
                            height: 20,
                            fontSize: "0.7rem",
                        }}
                    />
                </Stack>
                <Collapse in={expanded}>
                    <Box
                        paddingLeft={2}
                        paddingY={1}
                        sx={{
                            backgroundColor: theme.palette.gray[50],
                            borderRadius: 1,
                        }}
                    >
                        <Typography
                            component="pre"
                            variant="body2"
                            color={theme.palette.gray[900]}
                            sx={{
                                whiteSpace: "pre-wrap",
                                wordBreak: "break-word",
                                fontFamily: "inherit",
                                margin: 0,
                            }}
                        >
                            {value}
                        </Typography>
                    </Box>
                </Collapse>
            </Stack>
        );
    }

    return (
        <Stack
            direction="column"
            gap={0.5}
            paddingLeft={paddingLeft}
            paddingY={0.5}
        >
            <Stack direction="row" alignItems="center" gap={0.5}>
                <Typography
                    variant="body2"
                    fontWeight={500}
                    color={theme.palette.gray[500]}
                    fontSize="13px"
                >
                    {fieldName}
                </Typography>
                {schema.description && (
                    <Tooltip title={schema.description} arrow>
                        <Info fontSize="small" color="action" />
                    </Tooltip>
                )}
            </Stack>
            <Typography
                variant="body2"
                color={theme.palette.gray[900]}
                sx={{
                    fontFamily:
                        schema.type === "number" || schema.type === "integer"
                            ? "monospace"
                            : "inherit",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                }}
            >
                {String(value)}
            </Typography>
        </Stack>
    );
}
